// NPM MODULES
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Stack, Typography, Tabs, Tab, Grid } from '@mui/material';

// LAYOUT
import Layout from '../../components/layouts/Layout';

// ROUTES
import { WebsmsRoutes } from '../../routes/WebsmsRoutes';

// OUR COMPONENTS
import ContactList from '../../components/websms/addressbook/contactlist/ContactList';
import DistributionLists from '../../components/websms/addressbook/lists/DistributionLists';
import GlobalBlacklist from '../../components/websms/addressbook/blacklist/GlobalBlackList';
import Manage from '../../components/websms/addressbook/Manage';
import ContactImporter from '../../components/common/ContactImporter';
import ContactIcon from '../../components/icons/ContactIcon';
import UserListIcon from '../../components/icons/UserListIcon';
import CustomModal from '../../components/common/CustomModal';
import Loader from '../../components/common/Loader';

// MESSAGES
import AddressBookFormattedMessages from '../../messages/websms/AddressBookFormattedMessages';

// NETWORKING
import useAddressBookRequest from '../../requests/AddressBookRequest';

const AddressBook = ({ accessLevel, accessType, errorHandler }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('contacts');
  const [manage, setManage] = useState(false);
  const [distList, setDistList] = useState([]);
  const [showContactImport, setShowContactImport] = useState(false);
  const [contactList, setContactList] = useState([]);
  const [showContactImportModalSize, setShowContactImportModalSize] =
    useState(450);
  const { getSmsContacts, getDistLists } = useAddressBookRequest();

  const getData = useCallback(async () => {
    const data = await getSmsContacts(errorHandler);
    setContactList(data);
    const list = await getDistLists(errorHandler);
    setDistList(list);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  /*
    Sets which tab is currently being displayed
  */
  const handleSetTab = (event, newValue) => {
    setTab(newValue);
    setSelected(null);
    setShowContactImport(false);
  };

  /*
    Handles returning back from a child component
  */
  const handleBackClick = () => {
    setShowContactImport(false);
    setShowContactImportModalSize(450);
    setManage(false);
  };

  const showContactImporter = async () => {
    if (showContactImport) {
      let data = await getSmsContacts(errorHandler);
      setContactList(data);
    }
    setShowContactImport(!showContactImport);
  };

  return (
    <Layout
      routes={WebsmsRoutes}
      title={AddressBookFormattedMessages.addressBookTitle}
    >
      <Grid container sx={{ justifyContent: 'space-between' }}>
        <Grid size={12}>
          {isLoading ? (
            <Loader />
          ) : (
            <Box>
              <Box
                sx={{ borderBottom: 2, borderColor: 'divider', maxWidth: 800 }}
              >
                <Tabs value={tab} onChange={handleSetTab}>
                  <Tab
                    icon={<ContactIcon />}
                    label={
                      <Stack
                        sx={{
                          minWidth: { md: 200 },
                          paddingLeft: { xs: 0, md: 1.5 },
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'end',
                        }}
                      >
                        <Typography component="span">Contacts</Typography>
                        <Typography
                          component="span"
                          sx={{
                            display: { xs: 'none', md: 'inline-block' },
                          }}
                        >{`${contactList.length ?? 0} Contacts`}</Typography>
                      </Stack>
                    }
                    value="contacts"
                  />
                  <Tab
                    icon={<UserListIcon />}
                    label={
                      <Stack
                        sx={{
                          minWidth: { md: 150 },
                          paddingLeft: { xs: 0, md: 1.5 },
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'end',
                        }}
                      >
                        <Typography component="span">Lists</Typography>
                        <Typography
                          component="span"
                          sx={{
                            display: { xs: 'none', md: 'inline-block' },
                          }}
                        >{`${distList.length ?? 0} Lists`}</Typography>
                      </Stack>
                    }
                    value="lists"
                  />
                  {accessLevel > '0' && accessType !== '0' && (
                    <Tab label="Blacklist" value="blacklist" />
                  )}
                  <Tab label="Manage" value="manage" />
                </Tabs>
              </Box>

              <Box>
                {tab === 'contacts' && (
                  <ContactList
                    list={contactList}
                    showContactImporter={showContactImporter}
                    errorHandler={errorHandler}
                    accessLevel={accessLevel}
                    accessType={accessType}
                  />
                )}

                {tab === 'lists' && (
                  <DistributionLists
                    list={distList}
                    errorHandler={errorHandler}
                    accessLevel={accessLevel}
                    accessType={accessType}
                  />
                )}

                {tab === 'blacklist' &&
                  accessLevel > '0' &&
                  accessType !== '0' && (
                    <GlobalBlacklist
                      accessLevel={accessLevel}
                      accessType={accessType}
                      errorHandler={errorHandler}
                    />
                  )}

                {tab === 'manage' && (
                  <Manage
                    accessLevel={accessLevel}
                    accessType={accessType}
                    errorHandler={errorHandler}
                  />
                )}
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>

      <CustomModal
        title="Contacts"
        icon="contact"
        open={showContactImport}
        setOpen={setShowContactImport}
        handleClose={handleBackClick}
        width={showContactImportModalSize}
      >
        <ContactImporter
          showContactImporter={() => setShowContactImport(!showContactImporter)}
          accessLevel={accessLevel}
          accessType={accessType}
          errorHandler={errorHandler}
          handleBackClick={handleBackClick}
          setShowContactImportModalSize={setShowContactImportModalSize}
        />
      </CustomModal>
    </Layout>
  );
};

export default AddressBook;
